import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { t, formatNumber, LocaleController } from '@vitesse/base-ui/i18n';
import { showResultError } from '../../errors';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import '@vitesse/base-ui/elements/vitesse-gated';
import { IntegrationValueViewport } from './integration-value-viewport';
import type { Integration, IntegrationOp } from '../../../core/domain/integration';
import './topic-subscription-editor';
import './integration-values-lock';

/*
 * <vitesse-topic-viewport> — the integration TOPIC-kind viewport: a hierarchical
 * live-value namespace (MQTT/Kafka topics, NATS subjects, OPC-UA nodes) rendered
 * as the SAME SymbolGrid the controller symbol page uses, in TREE mode
 * (roots-first, per-node expand-on-demand). The integration schema is browsed on
 * demand — the backend pages a node's children only when expanded — so a
 * million/billion-node source stays O(viewport). Live values + sparklines stream
 * for the visible leaves via the window-follow value plane (the api-gateway drives
 * a viewport-scoped observe-lease).
 *
 * The grid itself lives in IntegrationValueViewport; this fixes treeMode = true
 * and adds the toolbar, because a broker screen needs two things a controller's
 * symbol page does not:
 *
 *   • THE DISCOVERY SCOPE, ON SCREEN. A broker cannot be asked what it holds, so
 *     discovery samples live traffic through a filter — and that filter decides
 *     what you are looking at. Leaving it buried in the edit dialog is actively
 *     misleading: the grid's own search box accepts the same wildcard syntax and
 *     means something completely different by it (a literal substring match over
 *     already-discovered paths), so a person who types `factory/line1/#` into the
 *     visible box gets zero rows and no explanation. Showing the active scope is
 *     what makes the two boxes tell themselves apart.
 *   • REDISCOVER + SUBSCRIPTIONS. A sample only hears what was publishing during
 *     it, so "look again" and "watch this specific topic regardless" are the two
 *     recovery paths, and neither existed.
 */

@customElement('vitesse-topic-viewport')
export class TopicViewport extends IntegrationValueViewport {
  protected readonly treeMode = true;

  /** The integration record — needed to re-apply (rediscover / save subscriptions)
   *  and to read the active discovery scope. Absent until the detail page has it. */
  @property({ attribute: false }) integration?: Integration;

  @state() private editorOpen = false;
  @state() private applying: '' | 'subs' | 'rediscover' = '';
  @state() private applyError = '';

  constructor() {
    super();
    new LocaleController(this);
  }

  // NO `static styles` HERE. The base renders in LIGHT DOM (it shares the global
  // symbolGrid.scss and the page's flex layout), and Lit only adopts static styles
  // into a shadow root — they would silently do nothing. The .int-bar rules live
  // in the page's own <style> beside .int-topic-paths, which is also why the
  // shared base-button stylesheet reaches these buttons with no workaround, unlike
  // the endpoint viewport's shadow root.

  /** Re-apply the descriptor (secrets omitted — the service merges the stored
   *  ones) with `targets` as the hand-added topics. Re-applying reconnects the
   *  driver, which re-runs discovery: so the same call is both "save my
   *  subscriptions" and "rediscover". The grid's structure stream pushes the new
   *  page once the indexer has ingested the result. */
  private async reapply(targets: IntegrationOp[], why: 'subs' | 'rediscover'): Promise<void> {
    const i = this.integration;
    if (!this.backend || !i || this.applying) return;
    this.applying = why;
    this.applyError = '';
    try {
      const res = await this.backend.api.applyIntegration(this.gatewayId, {
        integrationId: i.id,
        type: i.type,
        displayName: i.displayName,
        description: i.description,
        fields: i.fields,
        secrets: {},
        targets,
      });
      if (!res.ok) this.applyError = res.error;
    } catch (err) {
      this.applyError = err instanceof Error ? err.message : String(err);
      showResultError(err, t('integration.subsManage'));
    } finally {
      this.applying = '';
    }
  }

  /** The discovery filter actually in force. Absent means the driver's default —
   *  the WHOLE broker — which is the single most important thing to say out loud,
   *  since it is both the default and the widest possible scope. */
  private scopeLabel(): string {
    const f = this.integration?.fields?.discover_filter?.trim();
    return f ? f : t('integration.scopeAll');
  }

  protected renderToolbar() {
    const subs = this.integration?.targets ?? [];
    const count = this.integration?.schemaCount ?? 0;
    const ready = !!this.integration && !!this.backend;
    // Both buttons are one Apply (re-apply the descriptor), so they share one
    // lock — and the lock is on the button that OPENS the subscriptions dialog,
    // not on the dialog: letting someone fill in a form they cannot submit is a
    // worse refusal than not offering it (the gateway page's add-buttons do the
    // same). The values chip explains an EMPTY value column to a caller who may
    // browse the paths but not read them.
    return html`
      <div class="int-bar">
        <vitesse-gated .rpc=${Integrations.method.apply} inline>
          ${button({
            label: t('integration.subsManage'),
            icon: 'list-plus',
            size: 'sm',
            disabled: !ready,
            onClick: () => (this.editorOpen = true),
          })}
          ${button({
            label: this.applying === 'rediscover' ? t('integration.opsRediscovering') : t('integration.opsRediscover'),
            icon: 'refresh-cw',
            size: 'sm',
            disabled: !ready || this.applying !== '',
            onClick: () => void this.reapply(subs, 'rediscover'),
          })}
        </vitesse-gated>
        <vitesse-integration-values-lock></vitesse-integration-values-lock>
        <span class="scope" title=${t('integration.scopeHint')}>${t('integration.scopeLabel', { scope: this.scopeLabel() })}</span>
        ${count > 0 ? html`<span class="count">${t('integration.pathsCount', { n: formatNumber(count) })}</span>` : nothing}
        ${this.applyError && !this.editorOpen ? html`<span class="err">${this.applyError}</span>` : nothing}
      </div>
      <vitesse-topic-subscription-editor
        .open=${this.editorOpen}
        .subs=${subs}
        ?busy=${this.applying === 'subs'}
        .error=${this.applyError}
        @subs-change=${(e: CustomEvent<IntegrationOp[]>) => void this.reapply(e.detail, 'subs')}
        @dialog-close=${() => (this.editorOpen = false)}
      ></vitesse-topic-subscription-editor>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-topic-viewport': TopicViewport;
  }
}
